import { useCallback, useEffect, useMemo, useState } from 'react'
import Debug from 'debug'
import pm from 'picomatch'

import type { FileMeta } from '@jotta/utils/files'
import { getFilesFromDataTransferItemList } from '@jotta/utils/files'

import { useDocumentEventListener } from './useDocumentEventListener'

const debug = Debug('jotta:hooks:useDropZone')

interface UploadZoneProps {
  onAcceptFiles?: (files: FileMeta[]) => void
  onError: (path: string[]) => void
  filter?: {
    accept: (fileName: string) => boolean
    onRejectFiles: (files: FileMeta[]) => void
  }
  allowFolderUploads?: boolean
}

const ignoreMatcher = pm('**/.DS_Store')

function hasFiles(e: DragEvent) {
  return e.dataTransfer && e.dataTransfer.types.includes('Files')
}

export const useDropZone = ({
  onAcceptFiles,
  onError,
  filter,
}: UploadZoneProps) => {
  const accept = filter?.accept
  const onRejectFiles = filter?.onRejectFiles

  // dragging may involve multiple dragenter, dragleave and dragover events fired in different order in different browsers (remember to test all major browsers here),
  // especially if the drag dynamically displays new elements such as a drop zone. avoid flickering by clearing the drag state only if it has been stably false
  // for some time.
  const [isDragging, setIsDragging] = useState(false)
  const [delayedValue, setValue] = useState(false)
  useEffect(() => {
    if (isDragging) {
      setValue(true)
    } else {
      const timeout = setTimeout(() => {
        setValue(false)
      }, 100)
      return () => clearTimeout(timeout)
    }
  }, [isDragging])
  const isDraggingResult = isDragging || delayedValue

  const acceptFile = useMemo(() => {
    return ({ file }: FileMeta) => {
      const fileName = file.name
      const isAccepted = !accept || accept(fileName)
      const isIgnored = ignoreMatcher(fileName)
      return !isIgnored && isAccepted
    }
  }, [accept])

  const dragenter = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (hasFiles(e)) {
      setIsDragging(true)
    }
  }, [])

  const dragover = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (hasFiles(e)) {
      setIsDragging(true)
    }
  }, [])

  const dragleave = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const drop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      setIsDragging(false)

      if (!e.dataTransfer) {
        return
      }

      const files = await getFilesFromDataTransferItemList(
        e.dataTransfer.items,
        onError,
      )

      const acceptedFiles: FileMeta[] = []
      const rejectedFiles: FileMeta[] = []

      for (const file of files) {
        if (acceptFile(file)) {
          acceptedFiles.push(file)
        } else {
          rejectedFiles.push(file)
        }
      }

      if (onAcceptFiles && acceptedFiles.length) {
        onAcceptFiles(acceptedFiles)
      }
      if (onRejectFiles && rejectedFiles.length) {
        onRejectFiles(rejectedFiles)
      }
    },
    [onAcceptFiles, onRejectFiles, acceptFile, onError],
  )

  useDocumentEventListener('dragenter', dragenter)
  useDocumentEventListener('dragleave', dragleave)
  useDocumentEventListener('dragover', dragover)
  useDocumentEventListener('drop', drop)

  return isDraggingResult
}
